import { getPackItemChecked } from "~/services/packItemState.ts";
import { UNCATEGORIZED } from "~/services/utils.ts";
import { NamedEntity } from "~/types/NamedEntity.ts";
import { PackItem } from "~/types/PackItem.ts";

export type SectionGroup = {
  category: NamedEntity;
  title: string;
  items: PackItem[];
};

const ALL_ITEMS_TITLE = "All items";
const TWO_COLUMN_WIDTH = 800;
const THREE_COLUMN_WIDTH = 1200;

export const getItemColumnCount = (width: number, forceSingleColumn: boolean) => {
  if (forceSingleColumn || width <= TWO_COLUMN_WIDTH) return 1;
  return width > THREE_COLUMN_WIDTH ? 3 : 2;
};

const orderItems = (items: PackItem[], checkedItemsLast: boolean) =>
  checkedItemsLast
    ? [...items].sort((first, second) => Number(getPackItemChecked(first)) - Number(getPackItemChecked(second)))
    : items;

const groupItems = (items: PackItem[]) =>
  items.reduce<Map<string, PackItem[]>>((groups, item) => {
    const key = item.category || UNCATEGORIZED.id;
    const list = groups.get(key) ?? [];
    list.push(item);
    groups.set(key, list);
    return groups;
  }, new Map());

const createCategoryMap = (categories: NamedEntity[]) => {
  const map = new Map<string, NamedEntity>();
  map.set(UNCATEGORIZED.id, UNCATEGORIZED);
  for (const category of categories) {
    map.set(category.id, category);
  }
  return map;
};

const getOrderedCategories = (categories: NamedEntity[], groups: Map<string, PackItem[]>) => {
  const map = createCategoryMap(categories);
  for (const [id] of groups.entries()) {
    if (id && !map.has(id)) {
      const found = categories.find((category) => category.id === id);
      if (found) {
        map.set(id, found);
      }
    }
  }
  return [...map.values()].sort((a, b) => b.rank - a.rank);
};

export const buildSections = (
  items: PackItem[],
  categories: NamedEntity[],
  checkedItemsLast: boolean
): SectionGroup[] => {
  const grouped = groupItems(orderItems(items, checkedItemsLast));
  const showAllItemsTitle = grouped.size === 1 && grouped.has(UNCATEGORIZED.id);
  return getOrderedCategories(categories, grouped).map((category) => ({
    category,
    title: showAllItemsTitle && category.id === UNCATEGORIZED.id ? ALL_ITEMS_TITLE : category.name,
    items: grouped.get(category.id) ?? [],
  }));
};

export const getNextItemRank = (items: Pick<PackItem, "rank">[]) => {
  const ranks = items.map((item) => item.rank).filter((rank) => Number.isFinite(rank));
  return ranks.length ? Math.min(...ranks) - 1 : 0;
};

export const getTopItemRank = (items: Pick<PackItem, "rank">[], itemCount = 1) => {
  const ranks = items.map((item) => item.rank).filter((rank) => Number.isFinite(rank));
  return ranks.length ? Math.max(...ranks) + itemCount : 0;
};

export const getNextCategoryRank = (categories: Pick<NamedEntity, "rank">[]) => {
  const ranks = categories.map((c) => c.rank ?? 0).filter((rank) => Number.isFinite(rank));
  return ranks.length ? Math.min(...ranks) - 1 : 0;
};

export const getBulkEditTargets = (items: PackItem[]) => ({
  tickedItems: items.filter(getPackItemChecked),
  assignedItems: items.filter((item) => item.members.length > 0),
});
